import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ExamdojoAgGridModule } from '@examdojo/ag-grid';
import { BigQueryTableMetadata } from '@examdojo/auth';
import { DateTimeService } from '@examdojo/core/date-time';
import { ListViewSearchFieldComponent } from '@examdojo/core/list-view';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { ContentAnalyticsAgGridService, ContentAnalyticsData } from './content-analytics-ag-grid.service';

@Component({
  selector: 'exam-content-analytics-ag-grid',
  imports: [ExamdojoAgGridModule, ListViewSearchFieldComponent],
  templateUrl: './content-analytics-ag-grid.component.html',
  styleUrls: ['./content-analytics-ag-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ContentAnalyticsAgGridService],
  host: { class: 'flex h-full w-full flex-col' },
})
export class ContentAnalyticsAgGridComponent implements OnInit {
  constructor(
    private readonly agGridService: ContentAnalyticsAgGridService,
    private readonly destroyRef: DestroyRef,
    private readonly dateTimeService: DateTimeService,
  ) {
    // Set up search functionality
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchText) => {
          this.gridApi()?.setGridOption('quickFilterText', searchText ?? '');
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly data = signal<ContentAnalyticsData[]>([]);
  readonly isLoading = signal<boolean>(true);
  readonly tableMetadata = signal<BigQueryTableMetadata | null>(null);
  readonly gridApi = signal<GridApi<ContentAnalyticsData> | null>(null);
  readonly searchCtrl = new FormControl<string | null>('');

  get lastModifiedText(): string {
    const metadata = this.tableMetadata();
    if (!metadata?.lastModified || isNaN(metadata.lastModified.getTime())) {
      return '';
    }

    const timeAgo = this.dateTimeService.formatDateRelativeToNowShort(metadata.lastModified);
    return `last-modified: ${timeAgo}`;
  }

  get gridOptions() {
    return this.agGridService.gridOptions;
  }

  get columnDefs() {
    return this.agGridService.columnDefs;
  }

  ngOnInit(): void {
    this.loadData();
    this.loadTableMetadata();
  }

  onGridReady(event: GridReadyEvent<ContentAnalyticsData>): void {
    this.gridApi.set(event.api);

    // If we already have data loaded, ensure columns are sized
    if (!this.isLoading() && this.data().length > 0) {
      // We might need to re-set pinned data if it wasn't set during load because grid wasn't ready
      const totalsRow = this.agGridService.calculateTotalsRow(this.data());
      event.api.setGridOption('pinnedBottomRowData', [totalsRow]);
      this.sizeColumnsToFit();
    }
  }

  private loadTableMetadata(): void {
    this.agGridService
      .fetchTableMetadata()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((metadata) => {
          this.tableMetadata.set(metadata);
        }),
      )
      .subscribe();
  }

  private loadData(): void {
    this.isLoading.set(true);

    this.agGridService
      .getData()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ data, pinnedBottomRowData, isLoading }) => {
          this.data.set(data);
          this.isLoading.set(isLoading);

          if (!isLoading && this.gridApi()) {
            // Update pinned bottom row data via API if grid is ready
            // (Otherwise it might be handled by initial grid options if they were reactive, but here we set it manually)
            this.gridApi()?.setGridOption('pinnedBottomRowData', pinnedBottomRowData);
            this.sizeColumnsToFit();
          } else if (!isLoading) {
            // If grid not ready yet, update the service's gridOptions to include initial pinned data
            // This is a bit of a hack; ideally we rely on the gridReady event.
            this.agGridService.gridOptions.pinnedBottomRowData = pinnedBottomRowData;
          }
        }),
      )
      .subscribe();
  }

  private sizeColumnsToFit(): void {
    // Use a small delay to ensure the grid has finished rendering
    setTimeout(() => {
      this.gridApi()?.sizeColumnsToFit();
    }, 50);
  }
}
